import { ButtonPrimary, ButtonSecondary, ButtonTertiary } from "@nova/component-btn"
import { Dropdown } from "@nova/component-dropdown"
import { ReactNode, useMemo, useState } from "react"
import * as Icons from "@nova/component-icons"
import { Status } from "@nova/ds-component-status"
import { ReportDateRange, ReportTemplate } from "@nova/schemas"
import { Divider } from "components/Divider"
import {
    useDeleteReportTemplate,
    useDuplicateReportTemplate,
    useGenerateReport,
    useGenerateReportCSV,
    useReportTemplates,
} from "features/pilot-tracking/report-templates/ReportTemplateQueries"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { useRouter } from "pages"
import { getGenericWorkspacePage, getGenericWorkspacePageHeader } from "../WorkspaceRoutes"
import { Spinner } from "themes/default-tailwind"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { Card } from "features/pilot-tracking/MetricLayout"
import { useAircraft } from "features/pilot-tracking/aircraft/AircraftQueries"
import { getDisplayableAircraftData } from "@nova/constants"
import { ExpandableContent } from "@nova/component-expandable-content"
import { useAppSelector } from "integrations/redux/hooks"
import {
    selectAllRegisteredMembers,
    selectWorkspaceMembers,
} from "features/entity-caching/members/UserManagementSlice"

export const ReportsPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({
        title: "Reports",
        rightItems: [GenerateReportButton, AddTemplateButton],
    }),
    Content: ReportsPageContent,
    maxWidth: 1200,
})

function GenerateReportButton() {
    const router = useRouter("/workspace/:workspaceId")
    const { isEditor } = useWorkspacePermissions()
    if (!isEditor) return null
    return (
        <router.Link to={{ path: "/fleet/reports/generate" }}>
            <ButtonSecondary title="Generate Report" RightIcon={Icons.Laps} />
        </router.Link>
    )
}

function AddTemplateButton() {
    const router = useRouter("/workspace/:workspaceId")
    const { isEditor } = useWorkspacePermissions()
    if (!isEditor) return null
    return (
        <router.Link to={{ path: "/fleet/reports/create" }}>
            <ButtonPrimary title="Template" LeftIcon={Icons.Plus} />
        </router.Link>
    )
}

function ReportsPageContent() {
    const { data, isLoading } = useReportTemplates()
    const router = useRouter("/workspace/:workspaceId")
    const { isEditor } = useWorkspacePermissions()

    if (isLoading) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    if (!data?.length) {
        return (
            <div className="flex flex-col items-center gap-2">
                <h1 className="text-white">Create a Report Template</h1>
                <h5 className="text-gray-100">
                    Build templates to generate flight and COA reports for your organization.
                </h5>
                {isEditor && (
                    <router.Link to={{ path: "/fleet/reports/create" }}>
                        <ButtonPrimary className="mt-4" title="Template" LeftIcon={Icons.Plus} />
                    </router.Link>
                )}
            </div>
        )
    }

    return (
        <div className="flex flex-col items-center gap-3">
            {data.map((t) => (
                <ReportTemplateCard key={t.id} template={t} />
            ))}
        </div>
    )
}

const ReportTemplateCard = ({ template }: { template: ReportTemplate }) => {
    const pdf = useGenerateReport()
    const csv = useGenerateReportCSV()
    const duplicate = useDuplicateReportTemplate()
    const deleteHook = useDeleteReportTemplate()
    return (
        <Card className="flex flex-col !p-0 w-full max-w-[700px] relative">
            {(csv.isGenerating || pdf.isGenerating) && (
                <div className="absolute top-3 right-4 flex items-center gap-1 shrink-0">
                    <h5 className="text-gray-100">Generating&nbsp;Report</h5>
                    <Spinner />
                </div>
            )}
            <CardHeader template={template} />
            <div className="w-full h-px bg-white-200" />
            <CardBody template={template} />
            <div className="w-full h-px bg-white-200" />
            <CardFooter
                template={template}
                pdf={pdf}
                csv={csv}
                duplicate={duplicate}
                deleteHook={deleteHook}
            />
        </Card>
    )
}

const CardHeader = ({ template }: { template: ReportTemplate }) => {
    const lastModified = template.updatedAt ?? template.createdAt
    const modifiedByUser = useAppSelector(selectWorkspaceMembers).find(
        (e) => e.userId === (template.updatedBy ?? template.createdBy)
    )
    const formattedLastModified = new Date(lastModified).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    })
    const lastModifiedLabel = modifiedByUser?.fullName
        ? `${formattedLastModified} by ${modifiedByUser.fullName}`
        : formattedLastModified
    return (
        <div className="p-8 pb-4 flex items-end justify-between gap-3">
            <div className="flex flex-col gap-2 overflow-hidden">
                <Status variant="outline" type="neutral">
                    <small>{formatReportType(template.reportType)}</small>
                </Status>
                <div className="text-[22px] text-white telegraf truncate py-1">{template.name}</div>
            </div>
            <div className="flex flex-col items-end gap-0.5 shrink-0">
                <small className="text-gray-400">Last modified</small>
                <small className="text-gray-100">{lastModifiedLabel}</small>
            </div>
        </div>
    )
}

const CardBody = ({ template }: { template: ReportTemplate }) => {
    const users = useAppSelector(selectAllRegisteredMembers)
    const { aircraft, isLoading: isLoadingAircraft } = useAircraft()
    const includesPilots = useMemo(() => {
        return (
            template.filters.pilot_ids
                ?.map((pilotId) => users?.find((u) => u.id === pilotId)?.fullName)
                .filter((name): name is string => !!name) ?? ["All Pilots"]
        )
    }, [template.filters.pilot_ids, users])
    const includesDrones = useMemo(() => {
        if (isLoadingAircraft) return []
        return (
            template.filters.aircraft_ids
                ?.map((droneId) => {
                    const drone = aircraft.find((a) => a.id === droneId)
                    if (!drone) return undefined
                    const { displayName } = getDisplayableAircraftData(drone)
                    return displayName
                })
                .filter((name): name is string => !!name) ?? ["All Drones"]
        )
    }, [aircraft, isLoadingAircraft, template.filters.aircraft_ids])
    const notifies = useMemo(() => {
        return (
            template.notificationConfig?.email.recipients.addresses.map((email) => {
                const user = users?.find((u) => u.email === email)
                if (user && user.fullName) return user.fullName
                return email
            }) ?? []
        )
    }, [template.notificationConfig?.email.recipients.addresses, users])
    return (
        <div className="py-4 px-8 flex flex-col gap-6">
            {!!template.description && (
                <ReportTemplateCardField
                    title="Description"
                    value={<div className="text-gray-100">{template.description}</div>}
                />
            )}
            <ReportTemplateCardField
                title="Date Range"
                value={<div className="text-gray-100">{formatDateRange(template.dateRange)}</div>}
            />
            <ReportTemplateCardField
                title="Includes"
                value={
                    <div className="flex flex-col gap-1">
                        <div className="text-white pb-0.5">Pilots</div>
                        <ExpandableList data={includesPilots} />
                        <div className="py-0.5 mt-1.5 text-white">Drones</div>
                        <ExpandableList data={includesDrones} />
                    </div>
                }
            />
            {!!notifies.length && (
                <ReportTemplateCardField
                    title="Notifies"
                    value={<ExpandableList data={notifies} />}
                />
            )}
        </div>
    )
}

const CardFooter = ({
    template,
    pdf,
    csv,
    duplicate,
    deleteHook,
}: {
    template: ReportTemplate
    pdf: ReturnType<typeof useGenerateReport>
    csv: ReturnType<typeof useGenerateReportCSV>
    duplicate: ReturnType<typeof useDuplicateReportTemplate>
    deleteHook: ReturnType<typeof useDeleteReportTemplate>
}) => {
    const router = useRouter("/workspace/:workspaceId")
    const { isEditor } = useWorkspacePermissions()
    const modal = useConfirmationModalContext()
    const handleDelete = async () => {
        await modal.confirm({
            RenderComponent: getGenericConfirmationModalComponent({
                title: "Delete Report?",
                message: `Are you sure you want to delete ${template.name}?`,
            }),
            asyncConfirmCallback: async () => {
                await deleteHook.deleteReportTemplate(template.id)
            },
        })
    }
    const isProcessingSomething =
        duplicate.isDuplicating || deleteHook.isDeleting || pdf.isGenerating || csv.isGenerating
    return (
        <div className="p-4 flex justify-end gap-2">
            {isEditor && (
                <router.Link
                    to={{
                        path: "/fleet/reports/update/:reportTemplateId",
                        params: { reportTemplateId: template.id },
                    }}
                >
                    <ButtonTertiary title="Edit" LeftIcon={Icons.Edit} />
                </router.Link>
            )}
            <ButtonSecondary
                title="Download PDF"
                LeftIcon={Icons.Download}
                disabled={isProcessingSomething}
                onClick={() => pdf.generateReport({ id: template.id, name: template.name })}
            />
            <Dropdown disabled={isProcessingSomething}>
                <Dropdown.Trigger>
                    <ButtonSecondary
                        LeftIcon={Icons.ThreeDotsSettings}
                        disabled={isProcessingSomething}
                        className="!min-w-fit !px-2"
                    />
                </Dropdown.Trigger>
                <Dropdown.Menu position="bottom-right">
                    {isEditor && (
                        <>
                            <Dropdown.ButtonItem
                                className="flex items-center gap-3"
                                onClick={() => duplicate.duplicateReportTemplate({ template })}
                            >
                                <Icons.SmallIcon Icon={Icons.Copy} />
                                Duplicate
                            </Dropdown.ButtonItem>
                            <Divider className="bg-white-200 my-1" />
                        </>
                    )}
                    <Dropdown.ButtonItem
                        className="flex items-center gap-3"
                        onClick={() => pdf.generateReport({ id: template.id, name: template.name })}
                    >
                        <Icons.SmallIcon Icon={Icons.PDFCustom} />
                        <small>Download as PDF</small>
                    </Dropdown.ButtonItem>
                    <Dropdown.ButtonItem
                        className="flex items-center gap-3"
                        onClick={() =>
                            csv.generateReportCSV({ id: template.id, name: template.name })
                        }
                    >
                        <Icons.SmallIcon Icon={Icons.CSV} />
                        <small>Download as CSV</small>
                    </Dropdown.ButtonItem>
                    {isEditor && (
                        <>
                            <Divider className="bg-white-200 my-1" />
                            <Dropdown.ButtonItem
                                className="flex items-center gap-3 !text-red-500"
                                onClick={handleDelete}
                            >
                                <Icons.SmallIcon Icon={Icons.Trash} />
                                Delete
                            </Dropdown.ButtonItem>
                        </>
                    )}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    )
}

const ReportTemplateCardField = ({ title, value }: { title: string; value: ReactNode }) => {
    return (
        <div className="flex">
            <small className="xs text-gray-400 w-[135px] shrink-0 grow-0 translate-y-px">
                {title}
            </small>
            {value}
        </div>
    )
}

const ExpandableList = ({ data }: { data: string[] }) => {
    const [isExpanded, setIsExpanded] = useState(false)
    return (
        <div className="flex flex-col gap-0.5">
            {data.slice(0, 5).map((d, i) => (
                <div key={i} className="text-gray-100">
                    {d}
                </div>
            ))}
            {data.length > 5 && (
                <>
                    <ExpandableContent isOpen={isExpanded}>
                        <div className="flex flex-col gap-0.5">
                            {data.slice(5).map((d, i) => (
                                <div key={i} className="text-gray-100">
                                    {d}
                                </div>
                            ))}
                        </div>
                    </ExpandableContent>
                    <div
                        onClick={() => setIsExpanded((p) => !p)}
                        className="text-gray-300 hover:text-blue-500 underline cursor-pointer w-fit pt-0.5"
                    >
                        {isExpanded ? "Less" : `+${data.length - 5} More`}
                    </div>
                </>
            )}
        </div>
    )
}

const formatDateRange = (dateRange: ReportDateRange) => {
    switch (dateRange.type) {
        case "relative":
            return dateRange.days === 1 ? "Last 24 hours" : `Last ${dateRange.days} days`
        case "month":
            return dateRange.year === "current" && dateRange.month === "current"
                ? "This month"
                : `${dateRange.month} ${dateRange.year}`
        case "year_to_date":
            return "Year to date"
        case "last_calendar_month":
            return "Last calendar month"
        case "custom":
            return `${dateRange.start} - ${dateRange.end}`
    }
}

const formatReportType = (type: ReportTemplate["reportType"]) =>
    type === "flight_report" ? "Flight Report" : "COA Report"
